import { Award, Briefcase, Scale, Users, Shield, Target, CheckCircle2, FileText } from 'lucide-react';

export default function Team() {
  const expertise = [
    {
      icon: Scale,
      title: "Strategic Case Handling",
      description: "We evaluate every matter with precision, identifying risks, opportunities, and the most effective legal pathways through meticulous analysis and disciplined approach."
    },
    {
      icon: Briefcase,
      title: "Full Service Practice",
      description: "From civil litigation to corporate advisory, criminal defense to regulatory compliance, we provide comprehensive legal solutions across diverse practice areas."
    },
    {
      icon: Shield,
      title: "Strong Courtroom Advocacy",
      description: "Our advocates bring robust courtroom presence, persuasive drafting, and effective litigation strategies backed by thorough legal research and factual analysis."
    },
    {
      icon: Target,
      title: "Result Oriented Solutions",
      description: "We focus on securing timely, favorable, and enforceable outcomes while minimizing legal risk and cost exposure for our clients."
    },
    {
      icon: FileText,
      title: "Client Centric Strategy",
      description: "Every matter receives focused attention with tailored legal strategies aligned to client objectives, ensuring case specific handling from start to finish."
    }
  ];

  const stats = [
    { icon: Users, label: "Experienced Legal Advisors", value: "Expert" },
    { icon: Award, label: "High Value Disputes Handled", value: "Proven" },
    { icon: Briefcase, label: "Practice Areas", value: "Full Service" },
    { icon: Scale, label: "Case Success Rate", value: "Strong" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* HERO SECTION */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 sm:py-20 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full mb-6">
              <Users className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-xs sm:text-sm font-semibold tracking-widest uppercase">
                Our Work & Commitment
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Strategic. Reliable. <span className="text-yellow-400">Result Driven.</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 leading-relaxed">
              At Black Thorn Chambers, we are a full service law firm built on the principles of integrity, strategic thinking, and results driven advocacy. Our team comprises experienced legal advisors and advocates who bring strong courtroom presence, meticulous preparation, and commercially sound legal advice to every case we handle.
            </p>
          </div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section className="bg-white py-12 sm:py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="text-center group">
                  <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-slate-900 rounded-xl mb-4 group-hover:bg-yellow-500 transition-colors duration-300">
                    <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm sm:text-base text-slate-600">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Who We Are
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Black Thorn Chambers is known for its disciplined approach to law, deep understanding of legal frameworks, and unwavering commitment to client interests. We believe that every legal matter is unique, and accordingly adopt a client centric and case specific strategy, ensuring that each matter receives focused attention, thorough analysis, and proactive handling from start to finish.
            </p>
          </div>
        </div>

        <div className="text-center mb-12 sm:mb-16 mt-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            What Sets Us Apart
          </h2>
          <p className="text-slate-600 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            Our approach combines strategic precision with practical execution to deliver effective legal solutions
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {expertise.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={idx}
                className="bg-white rounded-xl p-6 sm:p-8 shadow-md border border-slate-200 hover:shadow-xl hover:border-yellow-500/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-slate-900 rounded-lg mb-4">
                  <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PRACTICE AREAS */}
      <section className="bg-white py-12 sm:py-16 md:py-20 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Areas of Practice
            </h2>
            <p className="text-slate-600 text-base sm:text-lg max-w-3xl mx-auto">
              Our multidisciplinary experience allows us to address complex legal issues with a holistic and forward looking perspective
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {[
              "Civil and Commercial Litigation",
              "Corporate and Business Advisory",
              "Contract Drafting and Negotiation",
              "Employment and Labour Law",
              "Property and Real Estate Matters",
              "Arbitration and Alternative Dispute Resolution",
              "Criminal Defense and Advisory",
              "Regulatory and Compliance Matters"
            ].map((area, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <CheckCircle2 className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-800 font-medium text-sm sm:text-base">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR STRENGTHS */}
      <section className="bg-slate-900 text-white py-12 sm:py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Our Proven Track Record
            </h2>
            <p className="text-slate-300 text-base sm:text-lg">
              Black Thorn Chambers has successfully handled numerous matters involving high value disputes, sensitive negotiations, and time critical legal interventions
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Strong Case Preparation</h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Supported by rigorous legal research and comprehensive factual analysis
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Effective Courtroom Advocacy</h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Persuasive drafting and compelling legal arguments in high stakes litigation
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Strategic Settlements</h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Negotiated resolutions where appropriate, balancing client interests with practical outcomes
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Swift Response</h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Handling urgent matters and interim reliefs with prompt action and strategic execution
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CLIENTS TRUST US */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Why Clients Trust Us
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {[
            { title: "Consistent Delivery of Results", desc: "Proven track record in securing favorable outcomes" },
            { title: "Transparent & Ethical Practice", desc: "Highest standards of professional integrity and accountability" },
            { title: "Business Minded Legal Advice", desc: "Practical, commercially sound guidance aligned with client goals" },
            { title: "Prompt Responsiveness", desc: "Accessible legal support when you need it most" },
            { title: "Long Term Partnerships", desc: "Building trusted relationships based on confidence and reliability" },
            { title: "Tailored Legal Strategies", desc: "Customized solutions, never generic approaches" }
          ].map((item, idx) => (
            <div key={idx} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="bg-gradient-to-br from-slate-50 to-white py-12 sm:py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4 sm:mb-6">
            Work With Blackthorn Chambers
          </h2>
          <p className="text-slate-600 text-base sm:text-lg md:text-xl mb-6 sm:mb-8 leading-relaxed">
            Whether you are seeking preventive legal advice or robust representation, Black Thorn Chambers is equipped to handle your matter with competence, discretion, and determination.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="px-6 sm:px-8 py-3 sm:py-4 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg text-sm sm:text-base"
            >
              Schedule Consultation
            </a>
            <a
              href="/practice-areas"
              className="px-6 sm:px-8 py-3 sm:py-4 bg-white hover:bg-slate-50 text-slate-900 font-semibold rounded-lg border-2 border-slate-900 transition-all duration-300 text-sm sm:text-base"
            >
              View Practice Areas
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
